import HTTP_CODES_ENUM from "./types/http-codes";
import type { FetchInitType } from "./types/fetch-params";
import { isSafeHttpMethod } from "./vercel-proxy";
import { resolveRequestTimeoutMs } from "./request-timeout";
import { buildRequestSignal } from "./build-request-signal";

// `RequestInit` plus the extra fields `fetch-retry` reads off the per-call
// `init` (see node_modules/fetch-retry/index.js). Declared explicitly because
// the wrapped `fetch` is `require`-imported (typed `any`), so these would
// otherwise be untyped.
export type RetryRequestInit = RequestInit & {
    retries?: number;
    retryOn?: number[];
};

// Method-aware retries.
// The `fetch-retry` wrapper retries ANY rejected promise (network error) by
// default, for EVERY HTTP method (it does not inspect the method). Because
// `POST /v1/auth/process-checkout` is NOT idempotent, an auto-retried POST after
// a post-backend connection drop can charge the customer multiple times. We
// therefore only auto-retry SAFE (idempotent) methods, and only for transient
// gateway/server errors.
//
// 429 is deliberately NOT in the retry list: Vercel serves `429 + text/html`
// as a bot / rate-limit interstitial that `useFetchBase` already recovers from
// by retrying through the same-origin proxy (see `detectVercelInterstitial`).
// Letting fetch-retry hammer the challenged cross-origin URL 3× first would
// only add latency before that proxy fast-path runs.
const SAFE_METHOD_MAX_RETRIES = 3;
const SAFE_METHOD_RETRY_ON_STATUSES = [
    HTTP_CODES_ENUM.INTERNAL_SERVER_ERROR,
    HTTP_CODES_ENUM.BAD_GATEWAY,
    HTTP_CODES_ENUM.SERVICE_UNAVAILABLE,
    HTTP_CODES_ENUM.GATEWAY_TIMEOUT,
];

// Assembles the `init` passed to the wrapped `fetch`:
//   • method-aware `retries` / `retryOn` (Fix 1),
//   • a per-request abort signal tuned by method + environment, merged with any
//     caller signal (Fix 3),
//   • base headers merged with caller-supplied headers (caller wins).
export function buildRetryRequestInit({
    init,
    headers,
    userAgent,
}: {
    init?: FetchInitType;
    headers: HeadersInit;
    userAgent: string | undefined;
}): RetryRequestInit {
    const isSafeMethod = isSafeHttpMethod(init?.method);

    return {
        ...init,
        retries: isSafeMethod ? SAFE_METHOD_MAX_RETRIES : 0,
        retryOn: isSafeMethod ? SAFE_METHOD_RETRY_ON_STATUSES : [],
        signal: buildRequestSignal(
            init?.signal,
            resolveRequestTimeoutMs(init?.method, userAgent),
        ),
        headers: mergeHeaders(headers, init?.headers),
    };
}

// Merges base headers with the caller's `init.headers` (caller wins). Uses
// `Headers` rather than object spread because spread silently drops a `Headers`
// instance or an array-of-tuples — both valid `HeadersInit` shapes a caller may
// pass — keeping only plain-object headers.
function mergeHeaders(base: HeadersInit, override?: HeadersInit): Headers {
    const merged = new Headers(base);
    if (override) {
        new Headers(override).forEach((value, key) => {
            merged.set(key, value);
        });
    }
    return merged;
}
